import * as React from 'react';
import { UserState } from 'app/reducers/state';
import { Spinner, Callout, Intent, Tab, Tabs, Button, H3, Dialog, H4, H5 } from '@blueprintjs/core';
import { each, find, clone, filter } from 'lodash';
import ReactTable from 'react-table';
import moment from 'moment';
import { PlayDetailsClassic } from '../PlayDetailsClassic';
import { launchDownloadLink } from 'app/utils/downloads';
import { DoneMarker } from '../DoneMarker';
import { NotDoneMarker } from '../NotDoneMarker';
import Highcharts from 'highcharts';
import highchartsMore from 'highcharts/highcharts-more';
import { executeQueryGetAuth, executeQueryPostAuth } from 'app/utils/api';
import { formatDate } from 'app/utils';
import { SingleFeedbackView } from '../SingleFeedbackView';

highchartsMore(Highcharts);

function sortStatBlock (stat: any) {
    // submitted
    
    // Handle undefined/null values
    if (!stat || !Array.isArray(stat)) {
        return stat;
    }
    
    stat.sort(function (a, b) {
        const aMoment = moment(a['submitted']);
        const bMoment = moment(b['submitted']);

        return !aMoment.isBefore(bMoment);
    });
    
    return stat;
}

interface Props {
	user: UserState;
	targetUserID: string;
	targetUserData?: any;
	forceNoEdit: boolean;
	hideDownloadPrompt?: boolean;
	showNavigation?: boolean;
	userTutorial: string;
	goToNext?: () => void;
	goToPrevious?: () => void;
};

interface State {
	needsClass: boolean;
	needsPayment: boolean;
	loadingOverview: boolean;
	loadingAllData: boolean;
	allData: any[];
	overviewData: any | undefined;
	blockFromEdit: boolean;
	selectedObjectiveSet: any | undefined;
	coachingData: any | undefined;
	showCoachingGate: boolean;
	unratedCoaching: any[];
}

export class SingleOverviewClassic extends React.Component<Props, State> {

	state = {
		needsClass: false,
		needsPayment: false,
		loadingOverview: false,
		loadingAllData: false,
		allData: [],
		overviewData: undefined,
		blockFromEdit: false,
		selectedObjectiveSet: undefined,
		coachingData: undefined,
		showCoachingGate: false,
		unratedCoaching: []
    }
    
    loadData = () => {
        executeQueryGetAuth(`data/progress/user/${this.props.targetUserID}/overview`, {}, this.props.user, (response) => {
            this.setState({
                overviewData: JSON.parse(response.text),
                loadingOverview: false
            });
        }); 

        executeQueryGetAuth(`data/all/results/${this.props.targetUserID}`, {}, this.props.user, (response) => {
            executeQueryGetAuth(`data/coaching/${this.props.targetUserID}`, {}, this.props.user, (response2) => {
                try {
                    const resp = JSON.parse(response2.text);
                    
                    // Check for unrated coaching
                    const unratedCoaching = resp.filter((coaching: any) => {
                        const support = coaching.support;
                        return support !== 'useful' && support !== 'not-useful';
                    });
    
                    // Only show coaching gate for students viewing their own data
                    const shouldShowCoachingGate = this.props.user.level === 1 && 
                        this.props.user.id.toString() === this.props.targetUserID && 
                        unratedCoaching.length > 0;

                    this.setState({
                        allData: JSON.parse(response.text),
                        loadingAllData: false,
                        coachingData: resp,
                        showCoachingGate: shouldShowCoachingGate,
                        unratedCoaching: unratedCoaching
                    })
                }
                catch(e) {
    
                }
            });
        }); 
    }

	componentDidMount() {
		this.setState({
			loadingOverview: true,
			loadingAllData: true
		});

        this.loadData();
	}

	componentDidUpdate(prevProps, prevState) {
		if (prevProps.targetUserID !== this.props.targetUserID) {
			this.setState({
				loadingOverview: true,
				loadingAllData: true,
				selectedObjectiveSet: undefined
			});
	
			this.loadData();
		}
	}

	handleSelectPayload = (gameID: string) => {
		const {
			allData
		} = this.state;

		const dataEntry = find(allData!, (entry: any) => {
			return entry["game-id"] === gameID
		});

		const block = gameID === this.getScenarioOverview(dataEntry['scenario'])['initial-game-id'];

		this.setState({
			selectedObjectiveSet: dataEntry,
			blockFromEdit: !block
		});

		window.scrollTo(0, 0);
	}

	getScenarioBestPlayData = (scenarioName: string) => {
		const {
			allData
		} = this.state;

		const scenarioOverview = this.getScenarioOverview(scenarioName);

		const dataEntry = find(allData!, (entry: any) => {
			return entry["game-id"] === scenarioOverview["best-game-id"]
		});

		return dataEntry;
	}

	getScenarioInitialPlayData = (scenarioName: string) => {
		const {
			allData
		} = this.state;

		const scenarioOverview = this.getScenarioOverview(scenarioName);

		const dataEntry = find(allData!, (entry: any) => {
			return entry["game-id"] === scenarioOverview["initial-game-id"]
		});

		return dataEntry;
	}

	getScenarioOverview = (scenarioName: string) => {
		const {
			loadingOverview,
			overviewData
		} = this.state;

		if (loadingOverview) return {}

		if (overviewData === undefined) return {}

		const scenarioData = overviewData![scenarioName];
		
		// Return empty object if scenario data doesn't exist
		return scenarioData || {};
	}

	getScenarioBestScore = (scenarioName: string) => {
		const scenarioOverview = this.getScenarioOverview(scenarioName);

		// Add safety check for scenarioOverview
		if (!scenarioOverview || scenarioOverview["best-score"] === "" || scenarioOverview["best-score"] === undefined || scenarioOverview["best-score"] === "0") {
			return "Not Played";
		}

		const bestScore = parseFloat(scenarioOverview["best-score"]);

		const percentageScore = bestScore;

		const scoreForDisplay = Math.round(percentageScore).toString();

		if (scoreForDisplay === "0") {
			return <span>
				-
			</span>
		}

		return <span>
			{scoreForDisplay + "%"}
		</span>
	}

	getScenarioClassicLPScore = (scenarioName: string) => {
		const scenarioOverview = this.getScenarioOverview(scenarioName);

		// Add safety check for scenarioOverview
		if (!scenarioOverview || scenarioOverview["best-score"] === "" || scenarioOverview["best-score"] === undefined || scenarioOverview["best-score"] === "0") {
			return "Not Played";
		}

		const bestScore = parseFloat(scenarioOverview["best-score"]);

		if (bestScore === 100.0) {
			return <DoneMarker />
		}

		return <NotDoneMarker />
	}

	// Return a graphic if the total-plays is atleast 1
	getScenarioHasPlayed = (scenarioName: string) => {
		const scenarioOverview = this.getScenarioOverview(scenarioName);

		// Add safety check for scenarioOverview
		if (!scenarioOverview || !scenarioOverview['total-plays']) {
			return <NotDoneMarker />
		}

		if (parseInt(scenarioOverview['total-plays']) >= 1) {
			return <DoneMarker />
		}
		else {
			return <NotDoneMarker />
		}
	}

	getTutorialHasPlayed = () => {
		const {
			userTutorial
		} = this.props;

		//@ts-ignore
		if (userTutorial === 1 || userTutorial === "1") {
			return <DoneMarker />
		}

		return <NotDoneMarker />
	}

	// Handle coaching rating
	handleCoachingRating = (coachingID: string, rating: string) => {
		console.log('Rating coaching:', coachingID, rating);
		console.log('Current unrated coaching:', this.state.unratedCoaching);
		
		// Send rating to server using the proper API function
		executeQueryPostAuth(`data/coaching/markread/${coachingID}?support=${rating}`, {}, this.props.user, (response) => {
			console.log('Successfully rated coaching, response:', response);
			// Remove this coaching from unrated list
			const updatedUnrated = this.state.unratedCoaching.filter((coaching: any) => 
				coaching.coachingID !== coachingID
			);
			
			console.log('Updated unrated coaching:', updatedUnrated);
			
			this.setState({
				unratedCoaching: updatedUnrated,
				showCoachingGate: updatedUnrated.length > 0
			});
			
			// Refresh the main data
			this.loadData();
		});
	}

	// Close coaching gate when all coaching is rated
	closeCoachingGate = () => {
		this.setState({
			showCoachingGate: false
		});
	}

	getScenarioInitilalScore = (scenarioName: string) => {
		const scenarioOverview = this.getScenarioOverview(scenarioName);

		// Add safety check for scenarioOverview
		if (!scenarioOverview || scenarioOverview["initial-score"] === "" || scenarioOverview["initial-score"] === undefined || scenarioOverview["initial-score"] === "0") {
			return "Not Played";
		}

		const bestScore = parseFloat(scenarioOverview["initial-score"]);

		const percentageScore = bestScore;

		const scoreForDisplay = Math.round(percentageScore).toString();

		if (scoreForDisplay === "0") {
			return <span>
				-
			</span>
		}

		return <span>
			{scoreForDisplay + "%"}
		</span>
	}


	renderOverviewCalloutEntry = (label: string, value: any) => {
		return <tr>
			<td style={{ width: '400px' }}>{label}</td>
			<td>{value}</td>
		</tr>
	}

	getDataByScenario = (scenarioName: string) => {
		const {
			allData
		} = this.state;

		//@ts-ignore
		if (allData === undefined || (Array.isArray(allData) && allData.length === 0)) return undefined;

		let gameIDAlreadyTracked: any[] = [];
		let filteredResults: any[] = [];

		each(allData, (dataEntry: any) => {
			if (dataEntry.scenario === scenarioName) {
				if (dataEntry['game-id'] !== null) {
					if (gameIDAlreadyTracked.includes(dataEntry['game-id']) === false) {
						gameIDAlreadyTracked.push(dataEntry['game-id']);
						filteredResults.push(dataEntry);
					}
					
				}
			}
		});

		return filteredResults;
	}

	updateJournalDataInline = (stage: string, payload: string, gameID: string) => {
		const {
			allData
		} = this.state;

		let newAllData: any[] = [];

		each(allData, (dataEntry: any) => {
			if (dataEntry['game-id'] === gameID) {
				if (stage === "pre") {
					dataEntry['preFeedback'] = payload;
				}
				else {
					dataEntry['postFeedback'] = payload;
				}
			}

			newAllData.push(dataEntry);
		});

		this.setState({
			allData: newAllData
		});
	}

	convertScenarioToFullName = (scenario: string) => {
		if (scenario === "Coaching") {
			return "Coaching";
		}
		
		if (scenario === 'tutorial') {
			return "Tutorial";
		}

		// Handle any literal "scenario" string values
		if (scenario === 'scenario') {
			return "Module";
		}

		if (scenario === 'classic_scenario_1') {
			return "Module 2 Explore";
		}

		if (scenario === 'classic_scenario_1_initial') {
			return "Module 2 Initial";
		}

		if (scenario === 'classic_scenario_1_assess') {
			return "Module 2 Assess";
		}

		if (scenario === 'classic_scenario_2_initial') {
			return "Module 3 Initial";
		}

		if (scenario === 'classic_scenario_2') {
			return "Module 3 Explore";
		}

		if (scenario === 'classic_scenario_2_assess') {
			return "Module 3 Assess";
		}

		if (scenario === 'classic_scenario_3_initial') {
			return "Module 4 Initial";
		}

		if (scenario === 'classic_scenario_3') {
			return "Module 4 Explore";
		}

		if (scenario === 'classic_scenario_3_assess') {
			return "Module 4 Assess";
		}

		if (scenario === 'classic_scenario_5_initial') {
			return "Module 5 Initial";
		}

		if (scenario === 'classic_scenario_5') {
			return "Module 5 Explore";
		}

		if (scenario === 'classic_scenario_5_assess') {
			return "Module 5 Assess";
		}

		if (scenario === 'scene_1_bc') {
			return "Get the Right Work Done";
		}

		if (scenario === 'scene_2_bc') {
			return "Getting Access to Hidden Ideas";
		}

		if (scenario === 'scene_4_bc') {
			return "Getting Commitment";
		}

		// Fallback for any unexpected scenario values
		return scenario;
	}

	renderTutorialTabPanel = () => {

	}

	renderOverviewTabPanel = (scenarioName: string) => {
		const {
			loadingAllData
		} = this.state;

		const allData = sortStatBlock(this.getDataByScenario(scenarioName));

		//@ts-ignore
		if (loadingAllData || allData === undefined || (Array.isArray(allData) && allData.length === 0)) {
			return <Spinner />
		}

		const playColumns = [{
			Header: "Module",
			accessor: 'scenario',
			Cell: (props) => {
				return <span>{this.convertScenarioToFullName(props.value)}</span>
			}
		}, {
			Header: "Played",
			accessor: "submitted",
			Cell: (props) => {
				return <span>
					{formatDate(props.value)}
				</span>
			}
		}, {
			Header: "Score",
			accessor: "results",
			Cell: (props) => {
				return <span>{Math.round(parseFloat(props.value))}</span>
			}
		}, {
			Header: "",
			accessor: "game-id",
			Cell: props => {
				return this.renderNormalButton(undefined, () => {
					this.handleSelectPayload(props.value);
				}, Intent.PRIMARY, "View Details", false);
			}
		}]

		return <div>
			<ReactTable data={allData} pageSize={allData.length} columns={playColumns} showPagination={false} showPageSizeOptions={false} />
		</div>
	}

	renderNormalButton = (rightIcon: any, onClick: any, intent: any, text: string, disabled: boolean) => {
		return <Button disabled={disabled} style={{ margin: '0px', width: '100%' }} rightIcon={rightIcon} onClick={() => { onClick(); }} intent={intent} text={text} />
	}

	getCanEditJournal = () => {
		const {
			forceNoEdit
		} = this.props;

		if (forceNoEdit) return false;

		return this.state.blockFromEdit;
	}


	getTranscriptData = () => {
		const {
			allData,
			coachingData
		} = this.state;

		//@ts-ignore
		if ((Array.isArray(allData) && allData.length === 0) || allData === undefined) return [];

		let data: any[] = clone(allData);
		let matches: any[] = [];

		// Remove rogue game entries
		data = filter(data, (entry) => {
			const matchFound = matches.includes(entry['game-id']);
			if(matchFound === true) return false;
			matches.push(entry['game-id']);
			return entry['game-id'] !== null;
		});

		if (coachingData !== undefined) {
			//@ts-ignore
			for (let i = 0; i < coachingData!.length; i++) {
				//@ts-ignore
				const _coaching = coachingData[i]['coaching'];
				//@ts-ignore
				const _created = coachingData[i]['created'];
				//@ts-ignore
				const _coachingID = coachingData[i]['coachingID'];
				//@ts-ignore
				const _support = coachingData[i]['support'];
				//@ts-ignore
				let _viewed = false;
				
				try {
					//@ts-ignore
					if (coachingData[i]['viewed'] === 1 || coachingData[i]['viewed'] === "1") {
						_viewed = true;
					}
				}
				catch(e) {

				}

				// Determine if coaching has been rated
				const isRated = _support === 'useful' || _support === 'not-useful';
				
				// For professors viewing student data, show "Unrated" instead of "Viewed - Please Rate"
				const isProfessorViewingStudent = this.props.user.level > 1 && this.props.user.id.toString() !== this.props.targetUserID;
				const statusText = _viewed ? (isRated ? `Rated: ${_support}` : (isProfessorViewingStudent ? 'Unrated' : 'Viewed - Please Rate')) : 'Has Not Been Viewed';

				data.push(
					{
						submitted: _created,
						scenario: 'Coaching',
						results: '-',
						preFeedback: 'Coaching Received: ' + _coaching,
						postFeedback: statusText,
						'game-id': '',
						coachingID: _coachingID,
						coachingText: _coaching,
						isRated: isRated,
						rating: _support
					}
				)
			}
		}

		try{
			data.sort((a: any, b: any) => {
				const aMoment = moment(a['submitted']);
				const bMoment = moment(b['submitted']);

				return aMoment.isBefore(bMoment) ? -1 : 1;
			});
		}
		catch(e) {
			// If data is a datatype that doesn't support .sort, that we missed
			// in our previous check then just try to gracifully fall back out of
			// this situation.
			return [];
		}

		return data;
	}

	renderTranscriptTab = () => {
		const allData = this.getTranscriptData();

		const playColumns = [{
			Header: "Module",
			accessor: 'scenario',
			width: 150,
			Cell: (props) => {
				return <span>{this.convertScenarioToFullName(props.value)}</span>
			}
		}, {
			Header: "Played",
			accessor: "submitted",
			width: 150,
			Cell: (props) => {
				return <span>
					{formatDate(props.value)}
				</span>
			}
		}, {
			Header: "Score",
			accessor: "results",
			width: 70,
			Cell: (props) => {
				if (props.value === "-") {
					return <span>-</span>
				}
				try {
					const valueAsDecimal = parseFloat(props.value);
					const asWholeNumber = Math.round(valueAsDecimal);

					return <span>{asWholeNumber}%</span>
				}
				catch(e) {
					return <span>-</span>
				}
			}
		}, ...(this.props.user.level === 3 ? [{
			Header: "Objectives Not Met",
			accessor: "objectives",
			width: 120,
			Cell: (props) => {
				// Classic version doesn't use objectives
				return <span>-</span>;
			}
		}] : []), {
			Header: "Pre Feedback",
			accessor: "preFeedback",
			Cell: props => {
				const feedback = props.value;

				return <SingleFeedbackView feedback={feedback} />
			}
		}, {
			Header: "Post Feedback",
			accessor: "postFeedback",
			Cell: props => {
				const feedback = props.value;

				return <SingleFeedbackView feedback={feedback} />
			}
		}];

		return <div>
			<ReactTable
				className="transcriptTable"
				data={allData}
				pageSize={allData ? allData!.length : 0}
				columns={playColumns}
				showPagination={false}
				getTrProps={(state, rowInfo, column) => {
					return {
						style: {
							minHeight: '66px'
						}
					}
				}}
				defaultSorted={[{
                    id   : 'submitted',
                    desc : true,
                }]}
				showPageSizeOptions={false}
			/>
		</div>
	}

	getStatsForScenario = (scenarioName: string) => {
        const {
            allData
        } = this.state;
        
        return filter(allData, stat => {
			//@ts-ignore
            return stat.scenario === scenarioName
        });
	}

	getUserName = () => {
		const {
			user,
			targetUserData
		} = this.props;

		let _user = user;

		if (targetUserData !== undefined && targetUserData !== null) {
			_user = targetUserData;
		}

		if (_user === undefined || _user === null) { return ''; }

		return `${_user.first} ${_user.last}`;
	}

	renderOverviewTab = () => {
		const {
			selectedObjectiveSet,
		} = this.state;

		const {
			user
		} = this.props;

		const tutorialDataSet = [{
			title: "Getting Started",
			val: this.getTutorialHasPlayed(),
			button: undefined
		},
		{
			title: "Getting Work Done",
			val: this.getScenarioClassicLPScore("scene_1_bc"),
			button: undefined
		},
		{
			title: "Getting Access to Hidden Ideas",
			val: this.getScenarioClassicLPScore("scene_2_bc"),
			button: undefined
		},
		{
			title: "Getting Commitment",
			val: this.getScenarioClassicLPScore("scene_4_bc"),
			button: undefined
		}];

		const scenario1DataSet = [{
			title: "Module 2 Initial Play",
			val: this.getScenarioInitilalScore("classic_scenario_1_initial"),
			button: this.getScenarioOverview("classic_scenario_1_initial")["initial-game-id"]
		}, {
			title: "Module 2 Best Explore Play",
			val: this.getScenarioBestScore("classic_scenario_1"),
			button: this.getScenarioOverview("classic_scenario_1")["best-game-id"]
		}, {
			title: "Module 2 Best Assess Play",
			val: this.getScenarioBestScore("classic_scenario_1_assess"),
			button: this.getScenarioOverview("classic_scenario_1_assess")["best-game-id"]
        }];

		const scenario2DataSet = [{
			title: "Module 3 Initial Play",
			val: this.getScenarioInitilalScore("classic_scenario_2_initial"),
			button: this.getScenarioOverview("classic_scenario_2_initial")["initial-game-id"]
		}, {
			title: "Module 3 Best Explore Play",
			val: this.getScenarioBestScore("classic_scenario_2"),
			button: this.getScenarioOverview("classic_scenario_2")["best-game-id"]
		}, {
			title: "Module 3 Best Assess Play",
			val: this.getScenarioBestScore("classic_scenario_2_assess"),
			button: this.getScenarioOverview("classic_scenario_2_assess")["best-game-id"]
		}];

		const scenario3DataSet = [{
			title: "Module 4 Initial Play",
			val: this.getScenarioInitilalScore("classic_scenario_3_initial"),
			button: this.getScenarioOverview("classic_scenario_3_initial")["initial-game-id"]
		}, {
			title: "Module 4 Best Explore Play",
			val: this.getScenarioBestScore("classic_scenario_3"),
			button: this.getScenarioOverview("classic_scenario_3")["best-game-id"]
		}, {
			title: "Module 4 Best Assess Play",
			val: this.getScenarioBestScore("classic_scenario_3_assess"),
			button: this.getScenarioOverview("classic_scenario_3_assess")["best-game-id"]
		}];

		const scenario5DataSet = [{
			title: "Module 5 Initial Play",
			val: this.getScenarioInitilalScore("classic_scenario_5_initial"),
			button: this.getScenarioOverview("classic_scenario_5_initial")["initial-game-id"]
		}, {
			title: "Module 5 Best Explore Play",
			val: this.getScenarioBestScore("classic_scenario_5"),
			button: this.getScenarioOverview("classic_scenario_5")["best-game-id"]
		}, {
			title: "Module 5 Best Assess Play",
			val: this.getScenarioBestScore("classic_scenario_5_assess"),
			button: this.getScenarioOverview("classic_scenario_5_assess")["best-game-id"]
		}];

		const scenarioOverviewColumnDef = [{
			Header: "",
			accessor: "title"
		}, {
			Header: "",
			accessor: "val"
		}, {
			Header: "",
			accessor: "button",
			Cell: (props) => {
				let disabled = (props.value === "" || props.value === undefined);

				return this.renderNormalButton(undefined, () => {
					this.handleSelectPayload(props.value);
				}, Intent.PRIMARY, "View Details", disabled);
			}
		}]


		return <span style={{ width: '50%', display: 'block', margin: 'auto' }}>
			<H3>{this.getUserName()}</H3>
			<span style={{display: 'block', marginBottom: '15px'}}>{formatDate(new Date().toUTCString())}</span>
			{selectedObjectiveSet ? <PlayDetailsClassic updateJournalDataInline={this.updateJournalDataInline} canEditJournal={this.getCanEditJournal()} selectedGame={selectedObjectiveSet!} user={user} /> : undefined}
			<Callout intent={Intent.NONE} title={"Module 1: Intent-based Communication"}>
				<ReactTable className={"minimal-overview-table"} data={tutorialDataSet} pageSize={tutorialDataSet.length} showPageSizeOptions={false} showPagination={false} sortable={false} columns={scenarioOverviewColumnDef} />
			</Callout>
			
			<Callout intent={Intent.NONE} title={"Module 2: Transforming Relationships"}>
				<ReactTable className={"minimal-overview-table"} data={scenario1DataSet} pageSize={scenario1DataSet.length} showPageSizeOptions={false} showPagination={false} sortable={false} columns={scenarioOverviewColumnDef} />
				
			</Callout>

			<Callout style={{ marginTop: '10px' }} intent={Intent.NONE} title={"Module 3: Opportunities in Conflict"}>
				<ReactTable className={"minimal-overview-table"} data={scenario2DataSet} pageSize={scenario2DataSet.length} showPageSizeOptions={false} showPagination={false} sortable={false} columns={scenarioOverviewColumnDef} />
				
			</Callout>

			<Callout style={{ marginTop: '10px' }} intent={Intent.NONE} title={"Module 4: Facilitating Change"}>
				<ReactTable className={"minimal-overview-table"} data={scenario3DataSet} pageSize={scenario3DataSet.length} showPageSizeOptions={false} showPagination={false} sortable={false} columns={scenarioOverviewColumnDef} />
				
			</Callout>

			<Callout style={{ marginTop: '10px' }} intent={Intent.NONE} title={"Module 5: Crisis and Opportunity"}>
				<ReactTable className={"minimal-overview-table"} data={scenario5DataSet} pageSize={scenario5DataSet.length} showPageSizeOptions={false} showPagination={false} sortable={false} columns={scenarioOverviewColumnDef} />
				
			</Callout>

			

			<Callout style={{ marginTop: '10px' }} intent={Intent.NONE} title={"View All Your Plays"}>
				<Tabs
					animate={true}
					id="score_view_tabs"
					key={"horizontal"}
					renderActiveTabPanelOnly={false}
					vertical={false}
				>
					<Tab id="tb_scenario_1_bc1" title="Get the Right Work Done" panel={this.renderOverviewTabPanel("scene_1_bc")} />
					<Tab id="tb_scenario_1_bc2" title="Getting Access to Hidden Ideas" panel={this.renderOverviewTabPanel("scene_2_bc")} />
					<Tab id="tb_scenario_1_bc3" title="Getting Commitment" panel={this.renderOverviewTabPanel("scene_4_bc")} />
					<Tab id="tb_scenario_1" title="Module 2 Explore" panel={this.renderOverviewTabPanel("classic_scenario_1")} />
					<Tab id="tb_scenario_1_a" title="Module 2 Assess" panel={this.renderOverviewTabPanel("classic_scenario_1_assess")} />
					<Tab id="tb_scenario_2" title="Module 3 Explore" panel={this.renderOverviewTabPanel("classic_scenario_2")} />
					<Tab id="tb_scenario_2_a" title="Module 3 Assess" panel={this.renderOverviewTabPanel("classic_scenario_2_assess")} />
					<Tab id="tb_scenario_3" title="Module 4 Explore" panel={this.renderOverviewTabPanel("classic_scenario_3")} />
					<Tab id="tb_scenario_3_a" title="Module 4 Assess" panel={this.renderOverviewTabPanel("classic_scenario_3_assess")} />
					<Tab id="tb_scenario_5" title="Module 5 Explore" panel={this.renderOverviewTabPanel("classic_scenario_5")} />
					<Tab id="tb_scenario_5_a" title="Module 5 Assess" panel={this.renderOverviewTabPanel("classic_scenario_5_assess")} />
				</Tabs>
			</Callout>
		</span>
	}

	render() {
		const {
			loadingAllData,
			loadingOverview
		} = this.state;

		if (loadingAllData || loadingOverview) {
			return <Spinner />
		}

		const {
			hideDownloadPrompt
		} = this.props;

		const shouldShowDownloadPrompt = (hideDownloadPrompt === true) ? false : true;

		return <span>
			{/* Coaching Gate Dialog */}
			<Dialog
				isOpen={this.state.showCoachingGate}
				onClose={this.closeCoachingGate}
				title="You Have New Coaching"
				canEscapeKeyClose={false}
				canOutsideClickClose={false}
				isCloseButtonShown={false}
				style={{width: '80%', maxWidth: '800px'}}
			>
				<div style={{padding: '20px'}}>
					<H4>Please read the following coaching feedback and let us know how we did before continuing:</H4>
					<p>Your feedback helps us improve the coaching experience for all students.</p>
					
					{this.state.unratedCoaching.map((coaching: any, index: number) => (
						<Callout key={coaching.coachingID} style={{marginBottom: '15px'}} intent={Intent.PRIMARY}>
							<H5>Coaching from {moment(coaching.created).format("LL")}</H5>
							<p style={{whiteSpace: 'pre-wrap', marginBottom: '15px'}}>
								{coaching.coaching}
							</p>
							<div>
								<Button 
									style={{ marginRight: '10px' }} 
									intent={Intent.PRIMARY} 
									onClick={() => {
										console.log('Button clicked! Coaching ID:', coaching.coachingID);
										this.handleCoachingRating(coaching.coachingID, "useful");
									}}
									text="This was Useful" 
								/>
								<Button 
									intent={Intent.WARNING} 
									onClick={() => {
										console.log('Button clicked! Coaching ID:', coaching.coachingID);
										this.handleCoachingRating(coaching.coachingID, "not-useful");
									}}
									text="Thanks, But It's Not Useful" 
								/>
							</div>
						</Callout>
					))}
					
					{this.state.unratedCoaching.length === 0 && (
						<div style={{textAlign: 'center', marginTop: '20px'}}>
							<Button 
								intent={Intent.SUCCESS} 
								onClick={this.closeCoachingGate}
								text="Continue to Activity Overview" 
							/>
						</div>
					)}
				</div>
			</Dialog>

			{shouldShowDownloadPrompt ?
				<Callout style={{ marginBottom: '20px' }} intent={Intent.PRIMARY} title="Download Your Software">
					If you are looking for the software you can download it here!
				<Button style={{ float: 'right', width: '200px' }} icon={"download"} large={true} text="Download" intent={Intent.PRIMARY} onClick={() => {
						launchDownloadLink();
					}} />
					<div className="clearfix" />
				</Callout> : undefined}

			<Tabs
				animate={true}
				id="overview_navigation"
				key={"horizontal2"}
				renderActiveTabPanelOnly={false}
				vertical={false}
			>
				<Tab id="overview_nav_1" title="Activity Overview" panel={this.renderOverviewTab()} />
				<Tab id="overview_nav_2" title="Transcript" panel={this.renderTranscriptTab()} />
			</Tabs>

		</span>
	}
}
